import { useAuth } from "@/auth";
import { PRO_BUYER_PROGRAM_ENABLED } from "@/config";
import { fetchMeProBuyer } from "@/features/pro-buyer-application/data/pro-buyer-application-client";
import {
  fetchAddressCompanyNames,
  fetchCartProBuyerLines,
  fetchOrderGroupsProBuyer,
  fetchOrdersProBuyer,
  fetchThreadCounterpartProBuyerPrice,
} from "@/features/pro-buyer-price/data/pro-buyer-price-client";
import { loadProductProBuyerPrice } from "@/features/pro-buyer-price/utils/product-price-batcher";
import { useQuery } from "@tanstack/react-query";

/**
 * WHO-5743. Every key starts here and carries the viewer id: these fields are
 * viewer-scoped, so one viewer's answer must never be served to another.
 */
export const PRO_BUYER_PRICE_QUERY_KEY = ["pro-buyer-price"] as const;

const VIEWER_STALE_MS = 5 * 60 * 1000;

/**
 * The single gate for every buyer-side surface: the program flag on this
 * build, a signed-in viewer (client-side only, so SSR HTML never carries a
 * Pro Buyer price) and `me.proBuyer` from the gateway.
 *
 * `settled` is true once the answer is final, so callers such as `view_item`
 * can wait for it without waiting forever on an anonymous viewer.
 */
const useProBuyerViewerLive = () => {
  const { token, isLoggedIn, isReady, userId } = useAuth();
  const enabled = isReady && isLoggedIn && !!token;

  const query = useQuery({
    queryKey: [...PRO_BUYER_PRICE_QUERY_KEY, "viewer", userId],
    queryFn: () => fetchMeProBuyer(token),
    enabled,
    staleTime: VIEWER_STALE_MS,
  });

  return {
    /** Program on for this build and the viewer is signed in. */
    signedIn: enabled,
    isProBuyer: enabled && query.data === true,
    settled: isReady && (!enabled || query.isFetched),
    token: enabled ? token : null,
    userId,
  };
};

/** `Product.proBuyerPrice` for one listing, only asked for a Pro Buyer. */
const useProductProBuyerPriceLive = (productId: string | null | undefined) => {
  const viewer = useProBuyerViewerLive();
  const enabled = viewer.isProBuyer && !!viewer.token && !!productId;

  const query = useQuery({
    queryKey: [
      ...PRO_BUYER_PRICE_QUERY_KEY,
      "product",
      viewer.userId,
      productId,
    ],
    queryFn: () =>
      loadProductProBuyerPrice(viewer.token ?? "", productId ?? ""),
    enabled,
  });

  return {
    price: enabled ? query.data ?? null : null,
    settled: viewer.settled && (!enabled || query.isFetched),
  };
};

/**
 * Seller side of a chat: `Thread.counterpartProBuyerPrice`. Asked for every
 * signed-in seller on a single conversation, since the seller is usually not
 * a Pro Buyer himself; the gateway fills it only when the other participant
 * is one and the seller's discount is on.
 */
const useThreadCounterpartProBuyerPriceLive = (
  threadId: string | null | undefined,
  isSeller: boolean
) => {
  const viewer = useProBuyerViewerLive();
  const enabled = viewer.signedIn && isSeller && !!threadId;

  const query = useQuery({
    queryKey: [...PRO_BUYER_PRICE_QUERY_KEY, "thread", viewer.userId, threadId],
    queryFn: () =>
      fetchThreadCounterpartProBuyerPrice(viewer.token, threadId ?? ""),
    enabled,
  });

  return enabled ? query.data ?? null : null;
};

/**
 * Pro Buyer fields per cart item. `revision` is anything that changes when
 * the gateway recalculated the cart (the totals), so each step re-reads.
 */
const useCartProBuyerLinesLive = (revision: string) => {
  const viewer = useProBuyerViewerLive();

  const query = useQuery({
    queryKey: [...PRO_BUYER_PRICE_QUERY_KEY, "cart", viewer.userId, revision],
    queryFn: () => fetchCartProBuyerLines(viewer.token),
    enabled: viewer.signedIn,
    staleTime: 0,
  });

  return viewer.signedIn ? query.data ?? null : null;
};

/** Pro Buyer order snapshots for the order groups on screen. */
const useOrderGroupsProBuyerLive = (groupIds: readonly string[]) => {
  const viewer = useProBuyerViewerLive();
  const enabled = viewer.signedIn && groupIds.length > 0;

  const query = useQuery({
    queryKey: [...PRO_BUYER_PRICE_QUERY_KEY, "orders", viewer.userId, groupIds],
    queryFn: () => fetchOrderGroupsProBuyer(viewer.token, groupIds),
    enabled,
  });

  return enabled ? query.data ?? null : null;
};

/** WHO-5950: seller side, Pro Buyer snapshot per sold order id. */
const useOrdersProBuyerLive = (orderIds: readonly string[]) => {
  const viewer = useProBuyerViewerLive();
  const enabled = viewer.signedIn && orderIds.length > 0;

  const query = useQuery({
    queryKey: [...PRO_BUYER_PRICE_QUERY_KEY, "sales", viewer.userId, orderIds],
    queryFn: () => fetchOrdersProBuyer(viewer.token, orderIds),
    enabled,
  });

  return enabled ? query.data ?? null : null;
};

/**
 * Company names per address id, or null while the gateway has no
 * `Address.companyName`: the address pop-up only offers the field when it
 * can be saved.
 */
const useAddressCompanyNamesLive = (enabled: boolean) => {
  const viewer = useProBuyerViewerLive();
  const active = enabled && viewer.isProBuyer;

  const query = useQuery({
    queryKey: [...PRO_BUYER_PRICE_QUERY_KEY, "addresses", viewer.userId],
    queryFn: () => fetchAddressCompanyNames(viewer.token),
    enabled: active,
  });

  return { names: active ? query.data ?? null : null, token: viewer.token };
};

/*
 * The program flag is a build-time constant, so each export is fixed for the
 * lifetime of the bundle: with the flag off every surface gets an inert hook,
 * which sends no request and needs no QueryClient (hook order never changes).
 */
const inertViewer = () => ({
  signedIn: false,
  isProBuyer: false,
  settled: true,
  token: null,
  userId: null,
});

export const useProBuyerViewer: typeof useProBuyerViewerLive =
  PRO_BUYER_PROGRAM_ENABLED ? useProBuyerViewerLive : inertViewer;

export const useProductProBuyerPrice: typeof useProductProBuyerPriceLive =
  PRO_BUYER_PROGRAM_ENABLED
    ? useProductProBuyerPriceLive
    : () => ({ price: null, settled: true });

export const useThreadCounterpartProBuyerPrice: typeof useThreadCounterpartProBuyerPriceLive =
  PRO_BUYER_PROGRAM_ENABLED
    ? useThreadCounterpartProBuyerPriceLive
    : () => null;

export const useCartProBuyerLines: typeof useCartProBuyerLinesLive =
  PRO_BUYER_PROGRAM_ENABLED ? useCartProBuyerLinesLive : () => null;

export const useOrderGroupsProBuyer: typeof useOrderGroupsProBuyerLive =
  PRO_BUYER_PROGRAM_ENABLED ? useOrderGroupsProBuyerLive : () => null;

export const useOrdersProBuyer: typeof useOrdersProBuyerLive =
  PRO_BUYER_PROGRAM_ENABLED ? useOrdersProBuyerLive : () => null;

export const useAddressCompanyNames: typeof useAddressCompanyNamesLive =
  PRO_BUYER_PROGRAM_ENABLED
    ? useAddressCompanyNamesLive
    : () => ({ names: null, token: null });
